import { DataTable } from '@/components/data-table/DataTable';
import { TableFilter } from '@/components/data-table/TableFilter';
import { TableHeaderWithSort } from '@/components/data-table/TableHeaderWithSort';
import useTableFilter from '@/components/data-table/useTableFilter';
import { LpohPagination } from '@/components/pagination/LpohPagination';
import { PageSize } from '@/components/pagination/PageSize';
import { Params } from '@/utils/types';
import { ColumnDef } from '@tanstack/react-table';
import { useLoaderData, useNavigation, useSearchParams } from 'react-router';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from './constants';
import { MeasurementGroup, MeasurementGroupListLoaderResponse } from './types';
import { OpenInNewWindowIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import useResetTableStateOnChange from '@/hooks/useResetTableState';
import { ROUTES } from '@/constants';
import { DEFAULT_DATE_TIME_FORMAT } from '@/utils/dates';
import { BreadcrumbRouterLink, RouterLink } from '@/components/link/Link';
import { maybeCellValue } from '@/utils/maybe-cell-value';
import { CreateMeasurementGroupForm } from '../create-measurement-group/CreateMeasurementGroupForm';
import { useState } from 'react';
import useTitle from '@/hooks/useTitle.ts';

export const MeasurementGroups = () => {
	useTitle("Measurement Groups");

	const columns: ColumnDef<MeasurementGroup>[] = [
		{
			accessorKey: 'id',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="MG ID" />
		},
		{
			accessorKey: 'indirectProcessObjectId',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="IPO ID" />
		},
		{
			accessorKey: 'name',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Name" />,
			cell: ({ row }) => (
				<div className="w-[400px] truncate" title={row.original.name}>
					{row.original.name}
				</div>
			)
		},
		{
			accessorKey: 'status',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Status" />
		},
		{
			accessorKey: 'latestMeasurementAt',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Last date" />,
			cell: ({ row }) =>
				maybeCellValue(row.original.latestMeasurementAt, val =>
					format(new Date(val as string), DEFAULT_DATE_TIME_FORMAT)
				)
		},
		{
			header: 'Actions',
			cell: ({ row }) => (
				<RouterLink
					className="flex justify-start items-center gap-1"
					to={ROUTES.getMeasurementGroupURL(row.original.id)}
				>
					<OpenInNewWindowIcon /> View
				</RouterLink>
			)
		}
	];

	const [searchParams] = useSearchParams();
	const navigation = useNavigation();
	const isNavigating = !!navigation.location;
	const page = searchParams.get(Params.page) || '1';
	const limit = searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
	const sortBy = searchParams.get(Params.sortBy) || '';
	const sortDirection = searchParams.get(Params.sortDirection) || '';
	const search = searchParams.get(Params.search) || '';

	const { handleFilterChange } = useTableFilter();

	const { measurementGroupList } = useLoaderData() as MeasurementGroupListLoaderResponse;

	const { items: measurementGroups, totalPages } = measurementGroupList;

	const [isFormOpen, setIsFormOpen] = useState(false);

	const handleToggleForm = () => {
		setIsFormOpen(!isFormOpen);
	};

	useResetTableStateOnChange({ observed: { page, limit, sortBy, sortDirection, search } });

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>Measurement Groups</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<div className="w-full flex gap-2 flex-col">
				<div className="flex justify-between md:flex-row flex-col-reverse gap-2">
					<TableFilter handleFilterChange={handleFilterChange} />
					<CreateMeasurementGroupForm isOpen={isFormOpen} handleToggleForm={handleToggleForm} />
				</div>

				<DataTable columns={columns} data={measurementGroups} isLoading={isNavigating} />

				<div className="flex justify-between">
					<PageSize pageSize={limit} options={PAGE_SIZES} />
					<LpohPagination currentPage={parseInt(page)} totalPages={totalPages} />
				</div>
			</div>
		</div>
	);
};
